/* eslint eslint-comments/no-use: off */

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {PullRequestStateIcons} from '@github-ui/item-picker-base/constants/icons'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import {type ItemGroup, maybePullRequestUrl} from '@github-ui/item-picker-base/shared'
import type {BranchPickerBranch} from '@github-ui/item-picker-branch/BranchPicker'
import {fetchBranchPickerSearch} from '@github-ui/item-picker-branch/fetch-branch-picker-search'
import {useQuery} from '@github-ui/react-query'
import type {PullRequestState} from '@github-ui/server-persisted-queries/types/PullRequestAndBranchPickerSearchServerQuery'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useDebounce} from '@github-ui/use-debounce'
import {GitBranchIcon, GitPullRequestIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {useCallback, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'

import {
  fetchPullRequestAndBranchPickerSearch,
  pullRequestAndBranchPickerSearchQueryKey,
} from './pull-request-and-branch-picker-search-fetch'
import type {
  PullRequestAndBranchPickerFragment,
  PullRequestPickerPullRequestNode,
} from './pull-request-and-branch-picker-search-query-types'
import styles from './PullRequestAndBranchPicker.module.css'

type PullRequestAndBranchPickerSharedProps = {
  onSelectionChange: (selected: Array<PullRequestPickerPullRequest | BranchPickerBranch>) => void
  shortcutsEnabled: boolean
  anchorElement?: (props: React.HTMLAttributes<HTMLElement>) => React.ReactElement
  subtitle?: string | React.ReactElement
  title?: string | React.ReactElement
  triggerOpen?: boolean
  onOpen?: () => void
  onClose?: () => void
  preventClose?: boolean
  loading?: boolean
  disabledPullRequestIds?: readonly string[]
  disabledPullRequestDescription?: string
}

export type PullRequestAndBranchPickerProps = PullRequestAndBranchPickerSharedProps & {
  repoNameWithOwner: string
  initialSelectedPrs: PullRequestPickerPullRequest[]
  initialSelectedBranches: BranchPickerBranch[]
  preventClose?: boolean
  // Identifies the issue this picker was opened from, for the `pull_request_and_branch_picker_search`
  // endpoint route (rate-limited/authorized per issue); unrelated to `repoNameWithOwner`, which is the
  // repo being searched and can differ once the caller picks another repo.
  issueOwner: string
  issueRepo: string
  issueNumber: number
}

type PullRequestPickerInternalProps = PullRequestAndBranchPickerSharedProps & {
  pullRequestData: PullRequestAndBranchPickerFragment | null
  branches: BranchPickerBranch[]
  initialSelectedBranches?: BranchPickerBranch[]
  initialSelectedPrs?: PullRequestPickerPullRequest[]
  onFilter: (value: string) => void
}

export type PullRequestPickerBaseProps = Omit<PullRequestPickerInternalProps, 'pullRequestData' | 'branches'> & {
  branchItems: BranchPickerBranch[]
  pullRequestItems: PullRequestPickerPullRequest[]
}

// Derived from PullRequestAndBranchPickerSearchQuery — the same query that feeds this component.
export type PullRequestPickerPullRequest = PullRequestPickerPullRequestNode

export const PullRequestAndBranchPickerPlaceholder = () => (
  <Button
    leadingVisual={GitPullRequestIcon}
    trailingVisual={TriangleDownIcon}
    aria-labelledby="pr-picker-label"
    disabled
  >
    {LABELS.selectPr}
  </Button>
)

const selectedGroup: ItemGroup = {groupId: 'selected'}
const suggestionsGroup: ItemGroup = {groupId: 'suggestions', header: {title: 'Suggestions', variant: 'filled'}}

export const LazyPullRequestAndBranchPicker = ({
  repoNameWithOwner,
  initialSelectedPrs,
  initialSelectedBranches,
  disabledPullRequestIds,
  loading,
  issueOwner,
  issueRepo,
  issueNumber,
  ...rest
}: PullRequestAndBranchPickerProps) => {
  const {addToast} = useToastContext()
  const [filter, setFilter] = useState('')
  const [debouncedFilter, setDebouncedFilter] = useState('')
  const [searchLoading, setSearchLoading] = useState(false)

  const [searchResults, setSearchResults] = useState<BranchPickerBranch[]>([])

  const tanstackQueries = getPullRequestSearchQueries(repoNameWithOwner, debouncedFilter)
  const tanstackQuery = useQuery({
    queryKey: pullRequestAndBranchPickerSearchQueryKey(issueOwner, issueRepo, issueNumber, tanstackQueries),
    queryFn: ({signal}) =>
      fetchPullRequestAndBranchPickerSearch(issueOwner, issueRepo, issueNumber, tanstackQueries, signal),
    enabled: Boolean(repoNameWithOwner),
  })

  useEffect(() => {
    if (!tanstackQuery.isError) return
    // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
    addToast({type: 'error', message: ERRORS.couldNotSearchPullRequests})
  }, [tanstackQuery.isError, addToast])

  const pullRequestData = tanstackQuery.data ?? null

  const fetchBranchesSearchData = useCallback(
    async (query: string) => {
      const repoNameWithOwnerParts = repoNameWithOwner.split('/')
      const owner = repoNameWithOwnerParts[0]
      const repo = repoNameWithOwnerParts[1]
      if (!owner || !repo) {
        return
      }
      try {
        const data = await fetchBranchPickerSearch(owner, repo, query)
        const repoBranches = (data.repository?.refs?.edges || [])
          .map(edge => edge?.node ?? null)
          .map(branch => branch as BranchPickerBranch)
          .filter(branch => branch !== null)

        setSearchResults(repoBranches)
      } catch {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({type: 'error', message: ERRORS.couldNotSearchBranches})
      } finally {
        setSearchLoading(false)
      }
    },
    [addToast, repoNameWithOwner],
  )

  const fetchPullRequestsSearchData = useCallback(
    (searchQuery: string) => {
      if (!repoNameWithOwner) return

      setSearchLoading(true)

      // The query itself is driven reactively by `debouncedFilter`; `tanstackQuery.isFetching` covers loading/errors.
      setDebouncedFilter(searchQuery)
    },
    [repoNameWithOwner],
  )

  const fetchSearchData = useCallback(
    (query: string) => {
      fetchBranchesSearchData(query)
      fetchPullRequestsSearchData(query)
    },
    [fetchPullRequestsSearchData, fetchBranchesSearchData],
  )

  function onFilter(value: string) {
    setFilter(value)
  }

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => fetchSearchData(nextValue),
    VALUES.pickerDebounceTime,
  )

  const fetchImmediately = useEffectEvent(() => {
    fetchSearchData(filter)
  })

  useEffect(() => {
    // intentionally running this not debounced on repoNameWithOwner changes
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchImmediately()
  }, [repoNameWithOwner])

  useEffect(() => {
    debounceFetchSearchData(filter)
  }, [debounceFetchSearchData, filter])

  return (
    <PullRequestAndBranchPickerInternal
      pullRequestData={pullRequestData}
      branches={searchResults}
      initialSelectedPrs={initialSelectedPrs}
      initialSelectedBranches={initialSelectedBranches}
      disabledPullRequestIds={disabledPullRequestIds}
      onFilter={onFilter}
      loading={Boolean(loading) || searchLoading || tanstackQuery.isFetching}
      {...rest}
    />
  )
}

export function PullRequestAndBranchPickerInternal({
  pullRequestData,
  branches: fetchedBranches,
  // eslint-disable-next-line @eslint-react/no-unstable-default-props
  initialSelectedPrs = [],
  // eslint-disable-next-line @eslint-react/no-unstable-default-props
  initialSelectedBranches = [],
  disabledPullRequestIds,
  ...rest
}: PullRequestPickerInternalProps) {
  const pullRequestItems = useMemo<PullRequestPickerPullRequest[]>(() => {
    const map = new Map<string, PullRequestPickerPullRequest>()
    const searchedPRs = getLinkedPullRequests(pullRequestData)
    for (const spr of searchedPRs) {
      map.set(spr.id, spr)
    }
    for (const ipr of initialSelectedPrs) {
      map.set(ipr.id, ipr)
    }
    return [...map.values()].sort(sortPrsByCreationDate)
  }, [pullRequestData, initialSelectedPrs])

  const branchItems = useMemo<BranchPickerBranch[]>(() => {
    const map = new Map<string, BranchPickerBranch>()
    for (const searchBranch of fetchedBranches) {
      map.set(searchBranch.id, searchBranch)
    }
    for (const initialBranch of initialSelectedBranches) {
      map.set(initialBranch.id, initialBranch)
    }
    return [...map.values()]
  }, [fetchedBranches, initialSelectedBranches])

  return (
    <PullRequestAndBranchPickerBase
      initialSelectedPrs={initialSelectedPrs}
      initialSelectedBranches={initialSelectedBranches}
      disabledPullRequestIds={disabledPullRequestIds}
      pullRequestItems={pullRequestItems}
      branchItems={branchItems}
      {...rest}
    />
  )
}

export function PullRequestAndBranchPickerBase({
  pullRequestItems,
  branchItems,
  // eslint-disable-next-line @eslint-react/no-unstable-default-props
  initialSelectedPrs = [],
  // eslint-disable-next-line @eslint-react/no-unstable-default-props
  initialSelectedBranches = [],
  disabledPullRequestIds,
  disabledPullRequestDescription,
  loading,
  onFilter,
  anchorElement,
  shortcutsEnabled,
  ...rest
}: PullRequestPickerBaseProps) {
  const responsiveOnNarrowEnabled = isFeatureEnabled('issues_react_select_panel_fullscreen_on_narrow')
  const initialSelectedItems = useMemo(
    () => [...initialSelectedPrs, ...initialSelectedBranches],
    [initialSelectedPrs, initialSelectedBranches],
  )
  const pullRequestPickerRef = useRef<HTMLButtonElement>(null)
  const disabledPullRequestIdSet = useMemo(() => new Set(disabledPullRequestIds ?? []), [disabledPullRequestIds])

  // Not showing the default branch or branches that already have a PR
  const filteredBranchItems = useMemo(() => {
    return branchItems.filter(
      branch => branch.id !== branch.repository.defaultBranchRef?.id && branch.associatedPullRequests.totalCount === 0,
    )
  }, [branchItems])

  const filterItems = useCallback(
    (value: string) => {
      onFilter(value)
    },
    [onFilter],
  )

  const getItemKey = useCallback((item: PullRequestPickerPullRequest | BranchPickerBranch) => item.id, [])

  const groups: ItemGroup[] = useMemo(() => {
    const itemGroups = []
    if (initialSelectedItems.length > 0) {
      itemGroups.push(selectedGroup)
    }
    if (
      branchItems.length + pullRequestItems.length > 0 &&
      pullRequestItems.length + branchItems.length - initialSelectedItems.length > 0
    ) {
      itemGroups.push(suggestionsGroup)
    }
    return itemGroups
  }, [branchItems.length, initialSelectedItems.length, pullRequestItems.length])

  const convertToItemProps = useCallback(
    (
      item: PullRequestPickerPullRequest | BranchPickerBranch,
    ): ExtendedItemProps<PullRequestPickerPullRequest | BranchPickerBranch> => {
      const isPr = item.__typename === 'PullRequest'
      const disabled = isPr && disabledPullRequestIdSet.has(item.id)
      const displayName = isPr ? item.title : item.name
      const displaySubtitle = isPr
        ? `${item.repository.nameWithOwner}#${item.number}${
            disabled && disabledPullRequestDescription ? ` - ${disabledPullRequestDescription}` : ''
          }`
        : LABELS.noPullRequest
      let icon
      if (isPr) {
        const PrIcon = getPrIcon(item)
        icon = (
          <span style={{color: getPrIconColor(item)}}>
            <PrIcon size={16} />
          </span>
        )
      } else {
        icon = <GitBranchIcon size={16} />
      }

      return {
        // this is a hack to make sure that we are using the prop
        id: `${item.id}`,
        groupId: [...initialSelectedBranches, ...initialSelectedPrs].find(r => r.id === item.id)
          ? selectedGroup.groupId
          : suggestionsGroup.groupId,
        children: (
          <div className={styles.pullRequestItemContainer}>
            <span>{displayName}</span>
            <span className={styles.subtitleText}>{displaySubtitle}</span>
          </div>
        ),
        source: item,
        leadingVisual: () => icon,
        disabled,
        className: styles.Item,
      }
    },
    [disabledPullRequestDescription, disabledPullRequestIdSet, initialSelectedBranches, initialSelectedPrs],
  )

  const renderAnchor = useCallback(
    ({...anchorProps}: React.HTMLAttributes<HTMLElement>) => {
      if (anchorElement) {
        return anchorElement(anchorProps)
      }

      return (
        <Button
          leadingVisual={GitPullRequestIcon}
          trailingVisual={TriangleDownIcon}
          {...anchorProps}
          aria-labelledby="pr-picker-label"
          ref={pullRequestPickerRef}
        >
          {initialSelectedItems.length > 0
            ? LABELS.getNumberOfSelectedPrsLabel(initialSelectedItems.length)
            : LABELS.selectPr}
        </Button>
      )
    },
    [anchorElement, initialSelectedItems.length],
  )

  const items = useMemo(() => {
    return [...pullRequestItems, ...filteredBranchItems]
  }, [pullRequestItems, filteredBranchItems])

  const showNoMatchMessage = useMemo(() => items.length === 0, [items.length])

  return (
    <div className={styles.pickerContainer}>
      <ItemPicker
        loading={loading}
        items={[...pullRequestItems, ...filteredBranchItems]}
        initialSelectedItems={initialSelectedItems}
        groups={groups}
        filterItems={filterItems}
        getItemKey={getItemKey}
        convertToItemProps={convertToItemProps}
        placeholderText={LABELS.searchPr}
        selectionVariant="multiple"
        selectPanelRef={pullRequestPickerRef}
        renderAnchor={renderAnchor}
        width="medium"
        resultListAriaLabel={'Pull request results'}
        height="large"
        improvedNoMatchAccessibility={showNoMatchMessage}
        noMatchMessage={
          showNoMatchMessage
            ? {
                title: 'No pull requests or branches were found',
                variant: 'empty',
                body: 'Try searching with a different query for results.',
              }
            : undefined
        }
        {...rest}
        responsiveOnNarrow={responsiveOnNarrowEnabled}
      />
    </div>
  )
}

function getLinkedPullRequests(data: PullRequestAndBranchPickerFragment | null): PullRequestPickerPullRequest[] {
  if (!data) return []

  // The empty-selection-set branch of the union has no `__typename` literal (just an index
  // signature), so `.__typename === 'PullRequest'` can't discriminate it away directly.
  const isPullRequest = (node: unknown): node is PullRequestPickerPullRequest =>
    (node as {__typename?: string} | null | undefined)?.__typename === 'PullRequest'

  if (data.resource && isPullRequest(data.resource)) {
    return [data.resource]
  }

  const {commenters, mentions, assignee, author, open} = data

  const allPrs = [
    ...(commenters?.nodes || []),
    ...(mentions?.nodes || []),
    ...(assignee?.nodes || []),
    ...(author?.nodes || []),
    ...(open?.nodes || []),
  ]

  const fetchedPrsMap = new Map<string, PullRequestPickerPullRequest>()

  for (const pr of allPrs) {
    if (pr && isPullRequest(pr)) {
      fetchedPrsMap.set(pr.id, pr)
    }
  }

  return Array.from(fetchedPrsMap.values())
}

function sortPrsByCreationDate(first: PullRequestPickerPullRequest, second: PullRequestPickerPullRequest) {
  const firstDate = new Date(first.createdAt)
  const secondDate = new Date(second.createdAt)

  if (firstDate < secondDate) {
    return 1
  } else if (firstDate > secondDate) {
    return -1
  } else {
    return 0
  }
}

const getPullRequestSearchQueries = (repoName: string, query = '') => {
  const sharedQuery = `is:pr repo:${repoName}`
  const trimmedQuery = query.trim()
  const queryIsUrl = maybePullRequestUrl(trimmedQuery)
  const queryWithSort = trimmedQuery.length > 0 ? `${trimmedQuery} sort:created-desc` : 'sort:created-desc'
  const modifiedQuery = ` ${queryWithSort}`

  return {
    commenters: `${sharedQuery} commenter:@me${modifiedQuery}`,
    mentions: `${sharedQuery} mentions:@me${modifiedQuery}`,
    assignee: `${sharedQuery} assignee:@me${modifiedQuery}`,
    author: `${sharedQuery} author:@me${modifiedQuery}`,
    open: `${sharedQuery} state:open${modifiedQuery}`,
    resource: queryIsUrl ? encodeURI(trimmedQuery) : '',
    queryIsUrl,
  }
}

function getPrIconColor(pullRequest: PullRequestPickerPullRequest) {
  const item = PullRequestStateIcons[getPrState(pullRequest.isDraft, pullRequest.isInMergeQueue, pullRequest.state)]
  return item.color
}

function getPrIcon(pullRequest: PullRequestPickerPullRequest) {
  const item = PullRequestStateIcons[getPrState(pullRequest.isDraft, pullRequest.isInMergeQueue, pullRequest.state)]
  return item.icon
}

export const getPrState = (isDraft: boolean, isInMergeQueue: boolean, state: PullRequestState) => {
  if (isDraft && state === 'OPEN') {
    return 'DRAFT'
  } else if (isInMergeQueue) {
    return 'IN_MERGE_QUEUE'
  } else if (state === 'CLOSED' || state === 'MERGED' || state === 'OPEN') {
    return state
  }

  throw new Error(`Unknown pull request state: ${state}`)
}
